/**
 * R232q5j: WorldMapV2 — кликабельная карта с zoom-in / zoom-out.
 *
 * Overview (M key): показывает aiwari-world-v2.png — общий план континента.
 * При клике на zone hot-region: переключается на zone-{id}.png детальный
 * вид. Кнопка «← Общий план» и Escape возвращают.
 *
 * R232q55: hot regions теперь авто-генерируются из ZONES catalogue
 * (data/zones.ts) — bounds в world-space переводятся в overview UV [0..1]
 * через worldToOverview(). Это значит:
 *   - все 44 зоны автоматически получают clickable overlay
 *   - bounds match server (zones-server.ts) бит-в-бит
 *   - tooltip показывает name.ru + level + кол-во квестов из QUESTS
 */

import { useEffect, useState } from 'react';
import { useNet } from '../state/net-store.js';
import { ZONES, zoneAt as findZoneAt, type Zone } from '../data/zones.js';
import { QUESTS } from '../data/quests.js';

interface HotRegion {
  /** ID для switching на детальный sub-map. Файл: maps/zone-{id}.png */
  id: string;
  /** Имя в hover-tooltip. */
  ru: string;
  /** Уровень рекомендуемый. */
  level?: { min: number; max: number };
  /** Тип для цветовой маркировки. */
  kind: 'capital' | 'village' | 'dungeon' | 'pvp' | 'boss';
  /** Bounding box на overview (0..1, x = left, y = top, w/h = доли ширины/высоты). */
  box: { x: number; y: number; w: number; h: number };
  /** Количество квестов в зоне (по giverPos из QUESTS). */
  questCount: number;
}

// World bounds (matches RealmRoom + zones-server). Declared early so the
// HOT_REGIONS IIFE below can reference them.
const WORLD_X_MIN = -3500, WORLD_X_MAX = 3500;
const WORLD_Z_MIN = -1750, WORLD_Z_MAX = 1750;

/** Legacy hand-tuned overlay boxes (R232q5j). Kept as fallback for zones
 *  whose Gemini map art doesn't perfectly match world-space bounds —
 *  these win over auto-derived bounds when present. */
const LEGACY_BOXES: Record<string, { x: number; y: number; w: number; h: number }> = {
  lionhold:         { x: 0.02, y: 0.42, w: 0.07, h: 0.13 },
  ironpyre:         { x: 0.04, y: 0.18, w: 0.07, h: 0.12 },
  moonwillow:       { x: 0.16, y: 0.08, w: 0.07, h: 0.11 },
  cogwarren:        { x: 0.04, y: 0.62, w: 0.07, h: 0.10 },
  crypthold:        { x: 0.30, y: 0.62, w: 0.07, h: 0.11 },
  coppergrove:      { x: 0.16, y: 0.42, w: 0.06, h: 0.08 },
  greenwald:        { x: 0.14, y: 0.52, w: 0.17, h: 0.09 },
  westmarch:        { x: 0.10, y: 0.36, w: 0.07, h: 0.09 },
  'redrock-hills':  { x: 0.18, y: 0.30, w: 0.07, h: 0.08 },
  duskhollow:       { x: 0.06, y: 0.30, w: 0.07, h: 0.08 },
  lochmod:          { x: 0.13, y: 0.16, w: 0.07, h: 0.10 },
  mireland:         { x: 0.20, y: 0.20, w: 0.07, h: 0.09 },
  thorndeep:        { x: 0.06, y: 0.78, w: 0.08, h: 0.10 },
  'sorrow-marsh':   { x: 0.22, y: 0.78, w: 0.08, h: 0.10 },
  'searing-gorge':  { x: 0.14, y: 0.74, w: 0.07, h: 0.07 },
  burnsteppe:       { x: 0.30, y: 0.78, w: 0.08, h: 0.08 },
  cinderlands:      { x: 0.40, y: 0.78, w: 0.08, h: 0.08 },
  hilsbrook:        { x: 0.30, y: 0.20, w: 0.07, h: 0.09 },
  arathih:          { x: 0.04, y: 0.85, w: 0.07, h: 0.08 },
  'tirisfal-glade': { x: 0.22, y: 0.22, w: 0.07, h: 0.07 },
  silverpine:       { x: 0.27, y: 0.22, w: 0.05, h: 0.07 },
  'west-plagueland':{ x: 0.32, y: 0.85, w: 0.06, h: 0.08 },
  'east-plagueland':{ x: 0.40, y: 0.88, w: 0.06, h: 0.08 },
  stormveil:        { x: 0.47, y: 0.42, w: 0.06, h: 0.12 },
  wartus:           { x: 0.91, y: 0.42, w: 0.07, h: 0.13 },
  stormcliff:       { x: 0.62, y: 0.62, w: 0.07, h: 0.10 },
  tikwave:          { x: 0.82, y: 0.78, w: 0.06, h: 0.07 },
  sharpridge:       { x: 0.78, y: 0.42, w: 0.06, h: 0.08 },
  sharpcrest:       { x: 0.62, y: 0.48, w: 0.17, h: 0.10 },
  wilds:            { x: 0.65, y: 0.36, w: 0.10, h: 0.07 },
  mulhorn:          { x: 0.68, y: 0.62, w: 0.07, h: 0.07 },
  stoneclaw:        { x: 0.62, y: 0.30, w: 0.08, h: 0.07 },
  ashpine:          { x: 0.72, y: 0.20, w: 0.06, h: 0.10 },
  needlespires:     { x: 0.78, y: 0.62, w: 0.06, h: 0.08 },
  dustmire:         { x: 0.62, y: 0.78, w: 0.08, h: 0.10 },
  wildshore:        { x: 0.86, y: 0.30, w: 0.06, h: 0.10 },
  sunsands:         { x: 0.86, y: 0.78, w: 0.06, h: 0.10 },
  'crater-of-wonder':{x: 0.50, y: 0.78, w: 0.08, h: 0.10 },
  crystalwastes:    { x: 0.55, y: 0.88, w: 0.08, h: 0.07 },
  moonglade:        { x: 0.48, y: 0.88, w: 0.05, h: 0.06 },
  shadowfen:        { x: 0.40, y: 0.04, w: 0.20, h: 0.10 },
};

/** Map zone.id from ZONES catalogue → overlay slot id. ZONES uses canonical
 *  ids (stormwind, orgrimmar, gnomeregan, …) while the legacy overlay used
 *  branding-clean aliases (lionhold, wartus, cogwarren, …). */
const ZONE_ID_TO_SLOT: Record<string, string> = {
  stormwind: 'lionhold',
  orgrimmar: 'wartus',
  ironforge: 'ironpyre',
  darnassus: 'moonwillow',
  undercity: 'crypthold',
  'thunder-bluff': 'stormcliff',
  gnomeregan: 'cogwarren',
  'sen-jin-village': 'tikwave',
  goldshire: 'coppergrove',
  northshire: 'greenwald',
  'razor-hill': 'sharpridge',
  senjin: 'tikwave',
  'stormveil-crossroads': 'stormveil',
  'anchorless-spawn': 'sharpcrest',
};

function classifyKind(zone: Zone): HotRegion['kind'] {
  if (zone.recommendedLevel.min >= 1 && zone.recommendedLevel.max >= 55) {
    if (zone.ownedBy && zone.bounds.maxX - zone.bounds.minX <= 800) return 'capital';
  }
  // Big level range (e.g. 1..60) = capital
  if (zone.recommendedLevel.min === 1 && zone.recommendedLevel.max === 60) return 'capital';
  if (zone.pvp === 'warzone') return 'boss';
  if (zone.pvp === 'contested') return 'pvp';
  if (zone.recommendedLevel.max <= 10) return 'village';
  return 'pvp';
}

function questCountForZone(zoneId: string): number {
  let n = 0;
  for (const q of QUESTS) {
    const z = findZoneAt(q.giverPos.x, q.giverPos.z);
    if (z && z.id === zoneId) n++;
  }
  return n;
}

const HOT_REGIONS: HotRegion[] = (() => {
  // Skip continent-scale zones — they cover the whole map and would swallow
  // every click. The two spawns + the crossroads are visualised by their
  // sub-zones (capitals + starter villages) instead.
  const SKIP = new Set(['tidal-spawn']);
  const out: HotRegion[] = [];
  for (const z of ZONES) {
    if (SKIP.has(z.id)) continue;
    const slot = ZONE_ID_TO_SLOT[z.id] ?? z.id;
    const legacy = LEGACY_BOXES[slot];
    let box: { x: number; y: number; w: number; h: number };
    if (legacy) {
      box = legacy;
    } else {
      // Auto-derive from world bounds → overview UV (matches worldToOverview).
      const x0 = (z.bounds.minX - WORLD_X_MIN) / (WORLD_X_MAX - WORLD_X_MIN);
      const x1 = (z.bounds.maxX - WORLD_X_MIN) / (WORLD_X_MAX - WORLD_X_MIN);
      const y0 = (z.bounds.minZ - WORLD_Z_MIN) / (WORLD_Z_MAX - WORLD_Z_MIN);
      const y1 = (z.bounds.maxZ - WORLD_Z_MIN) / (WORLD_Z_MAX - WORLD_Z_MIN);
      box = { x: Math.max(0, x0), y: Math.max(0, y0), w: Math.min(1, x1 - x0), h: Math.min(1, y1 - y0) };
    }
    out.push({
      id: slot,
      ru: z.name.ru,
      level: z.recommendedLevel,
      kind: classifyKind(z),
      box,
      questCount: questCountForZone(z.id),
    });
  }
  // Northern world-boss not in ZONES catalogue — keep as decorative entry.
  if (LEGACY_BOXES.shadowfen) {
    out.push({
      id: 'shadowfen',
      ru: 'Шейдоуфен (Властитель Шторма)',
      level: { min: 35, max: 60 },
      kind: 'boss',
      box: LEGACY_BOXES.shadowfen,
      questCount: 0,
    });
  }
  return out;
})();

const KIND_COLOUR: Record<HotRegion['kind'], string> = {
  capital: '#3B82F6',
  village: '#84CC16',
  dungeon: '#A855F7',
  pvp:     '#F87171',
  boss:    '#C9A96A',
};

const BASE = `${import.meta.env.BASE_URL}maps/`;

function worldToOverview(x: number, z: number): { x: number; y: number } {
  return {
    x: (x - WORLD_X_MIN) / (WORLD_X_MAX - WORLD_X_MIN),
    y: (z - WORLD_Z_MIN) / (WORLD_Z_MAX - WORLD_Z_MIN),
  };
}

export function WorldMapV2() {
  const [open, setOpen] = useState(false);
  const [zoneId, setZoneId] = useState<string | null>(null);
  const [hover, setHover] = useState<HotRegion | null>(null);
  const [mousePos, setMousePos] = useState<{ x: number; y: number } | null>(null);
  const players = useNet((s) => s.players);
  const selfId = useNet((s) => s.selfId);
  const self = selfId ? players[selfId] : null;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === 'm' || e.key === 'M' || e.key === 'ь' || e.key === 'Ь') {
        if (zoneId) { setZoneId(null); return; }
        setOpen((o) => !o);
      } else if (e.key === 'Escape') {
        if (zoneId) { setZoneId(null); return; }
        if (open) setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, zoneId]);

  if (!open) return null;

  // R232q35: switched to Gemini-generated maps (May 2026). Was *.jpg placeholders.
  const overviewSrc = `${BASE}aiwari-world-map.png`;
  const zoneSrc = zoneId ? `${BASE}zone-${zoneId}.png` : null;

  return (
    <div
      className="absolute inset-0 z-50 bg-black/90 backdrop-blur-sm flex flex-col items-center justify-center pointer-events-auto"
      onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
    >
      {/* Header bar */}
      <div className="absolute top-3 left-1/2 -translate-x-1/2 storm-glass bg-[#0B0907]/95 rounded-lg px-4 py-2 flex items-center gap-3"
           style={{ border: '1px solid #5C4520' }}>
        {zoneId && (
          <button
            type="button"
            onClick={() => setZoneId(null)}
            className="text-[12px] font-bold uppercase tracking-wider px-3 py-1 rounded bg-aiwari-bolt/20 text-[#E8C77B] border border-[#C9A96A] hover:bg-aiwari-bolt hover:text-aiwari-deep"
          >
            ← Общий план
          </button>
        )}
        <h2 className="text-[14px] font-bold tracking-wider" style={{ color: '#C9A96A', fontFamily: 'Cinzel, serif' }}>
          {zoneId
            ? (HOT_REGIONS.find((r) => r.id === zoneId)?.ru ?? 'Карта')
            : '🗺 Карта мира AIWARI'
          }
        </h2>
        <button onClick={() => setOpen(false)} className="text-[#E8DCC0]Muted hover:text-[#E8DCC0] text-xl leading-none px-2">×</button>
      </div>

      {/* Map canvas */}
      <div
        className="relative"
        style={{
          width: 'min(94vw, 1700px)',
          aspectRatio: '16/9',
          maxHeight: '88vh',
          background: '#1a0f0a',
          border: '4px solid #5C4520',
          borderRadius: 8,
          boxShadow: '0 0 32px rgba(0,0,0,0.85), inset 0 0 24px rgba(255,200,80,0.15)',
        }}
        onMouseMove={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          setMousePos({ x: e.clientX - r.left, y: e.clientY - r.top });
          if (zoneId) return;
          // Find hot region under cursor.
          const nx = (e.clientX - r.left) / r.width;
          const ny = (e.clientY - r.top) / r.height;
          let found: HotRegion | null = null;
          for (const reg of HOT_REGIONS) {
            if (nx >= reg.box.x && nx < reg.box.x + reg.box.w
             && ny >= reg.box.y && ny < reg.box.y + reg.box.h) {
              found = reg;
              break;
            }
          }
          setHover(found);
        }}
        onMouseLeave={() => { setHover(null); setMousePos(null); }}
        onClick={(e) => {
          if (zoneId) return;
          const r = e.currentTarget.getBoundingClientRect();
          const nx = (e.clientX - r.left) / r.width;
          const ny = (e.clientY - r.top) / r.height;
          for (const reg of HOT_REGIONS) {
            if (nx >= reg.box.x && nx < reg.box.x + reg.box.w
             && ny >= reg.box.y && ny < reg.box.y + reg.box.h) {
              setZoneId(reg.id);
              return;
            }
          }
        }}
      >
        <img
          src={zoneSrc ?? overviewSrc}
          alt={zoneId ?? 'AIWARI'}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            display: 'block',
            cursor: zoneId ? 'default' : (hover ? 'pointer' : 'crosshair'),
            imageRendering: 'auto',
          }}
          draggable={false}
        />

        {/* Hot region overlays — only on overview */}
        {!zoneId && HOT_REGIONS.map((r) => {
          const isHover = hover?.id === r.id;
          const color = KIND_COLOUR[r.kind];
          return (
            <div
              key={r.id}
              className="absolute pointer-events-none"
              style={{
                left: `${r.box.x * 100}%`,
                top: `${r.box.y * 100}%`,
                width: `${r.box.w * 100}%`,
                height: `${r.box.h * 100}%`,
                border: `2px solid ${isHover ? color : color + '00'}`,
                background: isHover ? `${color}28` : 'transparent',
                borderRadius: 6,
                transition: 'all 0.15s ease',
                boxShadow: isHover ? `0 0 18px ${color}88` : 'none',
              }}
            />
          );
        })}

        {/* R232q5w/x: Self position marker + heading arrow — на overview */}
        {!zoneId && self && (() => {
          const p = worldToOverview(self.pos.x, self.pos.z);
          if (p.x < 0 || p.x > 1 || p.y < 0 || p.y > 1) return null;
          // self.heading = атан2(forward) в radians; -π/2 чтобы указатель "north"
          // указывал вверх (в нашей world-down map орьентации).
          const angDeg = (self.heading ?? 0) * 180 / Math.PI;
          return (
            <div
              className="absolute pointer-events-none"
              style={{
                left: `${p.x * 100}%`,
                top: `${p.y * 100}%`,
                width: 0,
                height: 0,
                zIndex: 5,
              }}
            >
              {/* Pulsing background dot */}
              <div style={{
                position: 'absolute',
                left: -10, top: -10, width: 20, height: 20,
                background: '#C9A96A',
                border: '2px solid #0E0807',
                borderRadius: '50%',
                boxShadow: '0 0 14px rgba(251,191,36,0.95), 0 0 28px rgba(251,191,36,0.7)',
                animation: 'pulse-self 1.4s ease-in-out infinite',
              }} />
              {/* Direction arrow */}
              <div style={{
                position: 'absolute',
                left: -8, top: -22,
                width: 0, height: 0,
                borderLeft: '8px solid transparent',
                borderRight: '8px solid transparent',
                borderBottom: '14px solid #C9A96A',
                filter: 'drop-shadow(0 0 4px rgba(0,0,0,0.85))',
                transformOrigin: '8px 14px',
                transform: `rotate(${angDeg}deg) translateY(-2px)`,
              }} />
              <style>{`
                @keyframes pulse-self {
                  0%, 100% { transform: scale(1); opacity: 1; }
                  50% { transform: scale(1.5); opacity: 0.7; }
                }
              `}</style>
            </div>
          );
        })()}

        {/* Hover tooltip */}
        {hover && mousePos && (
          <div
            className="absolute z-10 pointer-events-none"
            style={{
              left: mousePos.x + 16,
              top: mousePos.y + 16,
              background: 'rgba(15,11,7,0.95)',
              border: `1.5px solid ${KIND_COLOUR[hover.kind]}`,
              borderRadius: 6,
              padding: '6px 12px',
              fontFamily: 'Cinzel, serif',
              fontSize: 13,
              color: KIND_COLOUR[hover.kind],
              fontWeight: 700,
              whiteSpace: 'nowrap',
              boxShadow: `0 4px 12px rgba(0,0,0,0.7), 0 0 12px ${KIND_COLOUR[hover.kind]}44`,
            }}
          >
            {hover.ru}
            {hover.level && (
              <span className="ml-2 text-[11px] text-[#E8DCC0]Dim font-normal">
                Ур. {hover.level.min}-{hover.level.max}
              </span>
            )}
            {hover.questCount > 0 && (
              <span className="ml-2 text-[11px] font-normal" style={{ color: '#E8C77B' }}>
                · {hover.questCount} квест{hover.questCount === 1 ? '' : (hover.questCount < 5 ? 'а' : 'ов')}
              </span>
            )}
            <div className="text-[10px] text-[#E8DCC0]Muted mt-0.5 italic">кликни — приблизить</div>
          </div>
        )}
      </div>

      {/* Footer help */}
      <div className="absolute bottom-3 text-[11px] text-[#E8DCC0]Muted">
        {zoneId
          ? 'Esc или ← — назад к общему плану'
          : 'Кликни на регионе — приблизить · M или Esc — закрыть'
        }
      </div>
    </div>
  );
}
